import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { BeeChatClient, IChat, IChatMessage } from './BeeChatClient';
import { ICareMapKey } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import removeMarkdown from 'remove-markdown';
import { IBeaConversationState, IBeaConversationStateData, IChatRowSummary } from './IBeaConversationState';
import { BeaChatFormatter } from './BeaChatFormatter';
import { stripCitations } from './stripCitations';

export const BEA_WELCOME_MESSAGE = `Hi, I'm Bea! I'm here to help with care questions, resources, or just to listen. How can I help?`;

export class BeeChatState extends State<IBeaConversationStateData> implements IBeaConversationState {
    static inject = () => [
        BeeChatClient,
        APIClient,
        CareMapState,
        BeaChatFormatter
    ];

    private abortController: AbortController | null = null;

    private _extractionInFlight = false;
    private _extractionPending = false;

    constructor(
        private chatClient: BeeChatClient,
        private apiClient: APIClient,
        private careMapState: CareMapState,
        private formatter: BeaChatFormatter
    ) {
        super({
            chats: [],
            activeChat: null,
            messages: [],
            isSending: false,
            isStreaming: false,
            streamingText: '',
            currentView: 'chat'
        });
    }

    cancelStream = () => {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    private ensureWelcomeMessage = (messages: IChatMessage[]): IChatMessage[] => {
        return this.formatter.withWelcome({ messages, welcome: BEA_WELCOME_MESSAGE });
    };

    private getCareMapKey = (): ICareMapKey | undefined => {
        const careMap = this.careMapState.getMostRecentCaremap();
        if (!careMap) {
            return undefined;
        }
        return {
            userId: careMap.userId,
            careMapId: careMap.careMapId
        };
    };

    loadChats = async () => {
        const chats = await this.chatClient.getChats();
        this.setState({ chats: this.formatter.cleanLoaded(chats) });
    };

    startNewConversation = () => {
        const welcomeMessage: IChatMessage = {
            role: 'assistant',
            content: BEA_WELCOME_MESSAGE,
            createdAt: new Date().toISOString()
        };

        this.setState({
            activeChat: null,
            messages: [welcomeMessage],
            currentView: 'chat'
        });
    };

    selectChat = (chatId: string) => {
        const chat = this.state.chats.find(c => c.chatId === chatId);
        if (chat) {
            // First update the messages while still on list view
            this.setState({
                activeChat: chat,
                messages: this.ensureWelcomeMessage(chat.messages)
            });
            // Delay allows React render and browser paint to complete before animating
            setTimeout(() => {
                this.setState({ currentView: 'chat' });
            }, 150);
        }
    };

    showConversationList = () => {
        this.setState({ currentView: 'list' });
    };

    showChat = () => {
        this.setState({ currentView: 'chat' });
    };

    sendMessage = async (text: string) => {
        const userMessage: IChatMessage = {
            role: 'user',
            content: text,
            createdAt: new Date().toISOString()
        };

        // Add user message to UI immediately
        const updatedMessages = [...this.state.messages, userMessage];
        this.setState({
            messages: updatedMessages,
            isSending: true,
            isStreaming: false,
            streamingText: ''
        });

        try {
            const entry = await this.apiClient.entry();
            const careMapKey = this.getCareMapKey();

            await this.sendMessageWithStreaming({
                streamUrl: entry.links.chatStream.href,
                message: text,
                chatId: this.state.activeChat?.chatId,
                careMapKey,
                updatedMessages
            });
        } catch (error) {
            console.error('Error sending message to Bea:', error);

            const errorMessage: IChatMessage = {
                role: 'assistant',
                content: 'I apologize, but I encountered an error. Please try again.',
                createdAt: new Date().toISOString()
            };

            this.setState({
                messages: [...updatedMessages, errorMessage],
                isSending: false,
                isStreaming: false,
                streamingText: ''
            });
        }
    };

    private sendMessageWithStreaming = async (params: {
        streamUrl: string;
        message: string;
        chatId?: string;
        careMapKey?: ICareMapKey;
        updatedMessages: IChatMessage[];
    }) => {
        const { streamUrl, message, chatId, careMapKey, updatedMessages } = params;

        let streamingText = '';
        let newChatId: string | undefined;

        // Cancel any existing stream before starting a new one
        this.cancelStream();
        this.abortController = new AbortController();

        this.setState({
            isSending: false,
            isStreaming: true,
            streamingText: ''
        });

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            for await (const event of this.chatClient.streamChat({
                streamUrl,
                message,
                chatId,
                careMapKey,
                signal: this.abortController.signal,
                metadata: { timezone }
            })) {
            if (event.type === 'chat_created') {
                newChatId = event.chatId;
            } else if (event.type === 'text' && event.text) {
                streamingText += event.text;
                this.setState({ streamingText: stripCitations(streamingText, true) });
            } else if (event.type === 'done') {
                // Streaming complete - finalize the message
                const assistantMessage: IChatMessage = {
                    role: 'assistant',
                    content: stripCitations(streamingText, false) || 'I apologize, but I could not generate a response.',
                    createdAt: new Date().toISOString()
                };

                const finalMessages = [...updatedMessages, assistantMessage];
                const finalChatId = newChatId || chatId;

                // Create or update the chat in local state
                let updatedChat = this.state.activeChat;
                if (finalChatId) {
                    updatedChat = {
                        ...updatedChat,
                        chatId: finalChatId,
                        messages: finalMessages.filter(m => m.content !== BEA_WELCOME_MESSAGE),
                        lastMessageAt: new Date().toISOString()
                    } as IChat;
                }

                let updatedChats = this.state.chats;
                if (updatedChat) {
                    updatedChats = updatedChats.filter(c => c.chatId !== updatedChat!.chatId);
                    updatedChats = [updatedChat, ...updatedChats];
                }

                this.setState({
                    activeChat: updatedChat,
                    messages: this.ensureWelcomeMessage(finalMessages.filter(m => m.content !== BEA_WELCOME_MESSAGE)),
                    chats: updatedChats,
                    isStreaming: false,
                    streamingText: ''
                });
            } else if (event.type === 'error') {
                throw new Error(event.message || 'Stream error');
            }
        }
        } finally {
            this.abortController = null;
        }
    };

    getTitle = (chat: IChat): string => this.formatter.getTitle(chat);

    getSummary = (chat: IChat): IChatRowSummary => {
        const lastMessage = chat.messages[chat.messages.length - 1];
        return { text: lastMessage ? removeMarkdown(lastMessage.content) : 'New conversation', isPersonName: false };
    };

    hasChats = (): boolean => {
        return this.state.chats.length > 0;
    };

    extractMemoryIfActive = async (): Promise<void> => {
        // Concurrency guard: close() and APP_BACKGROUNDED can fire near-simultaneously
        // (e.g., user closes the modal as the OS backgrounds the app). Without this guard,
        // two concurrent POSTs would extract the same chat content twice and produce
        // duplicate memory items (each call generates a fresh memoryId, so DAO-level
        // dedup does not catch it). If a trigger fires while one extraction is already
        // running, we set a pending flag and re-fire after completion so any messages
        // sent during the window still get extracted.
        if (this._extractionInFlight) {
            this._extractionPending = true;
            return;
        }
        const chat = this.state.activeChat;
        if (!chat?.chatId) {
            return;
        }
        if (!chat.messages.some(m => m.role === 'user')) {
            return;
        }
        this._extractionInFlight = true;
        try {
            const entry = await this.apiClient.entry();
            const link = entry.links.chatExtract;
            if (!link) {
                return;
            }
            await this.chatClient.extractChat({
                link,
                chatId: chat.chatId,
                careMapKey: this.getCareMapKey()
            });
        } catch (error) {
            // Fire-and-forget: server-side errors are already logged via Sentry. Client-side
            // network errors are not actionable from the close-chat UX, and surfacing them
            // would interrupt the modal animation. Swallow with a console warn so a developer
            // hitting this in dev tools sees something.
            console.warn('Bea memory extraction failed:', error);
        } finally {
            this._extractionInFlight = false;
            if (this._extractionPending) {
                this._extractionPending = false;
                // Re-fire to capture any content that arrived during the previous run.
                // Not awaited — caller (close/background handler) does not need to block on it.
                void this.extractMemoryIfActive();
            }
        }
    };
}
